/**
 * Geolocation helpers for centering the map on the user.
 */

import {
	GEO_OPTIONS,
	INITIAL_ZOOM,
	STORED_LOCATION_KEY,
	getLocationRadiusMeters,
	isMobileViewport,
} from './config.js';
import {
	map,
	userLocationMarker,
	radiusCircle,
	AdvancedMarkerElementClass,
	PinElementClass,
	setUserLocationMarker,
	setRadiusCircle,
	setMarkerClasses,
} from './state.js';
import { updateMarkerVisibility } from './marker-visibility.js';

/**
 * Reads a previously saved user location from localStorage.
 *
 * @return {google.maps.LatLngLiteral|null} Stored lat/lng, or null if missing/invalid.
 */
function loadStoredLocation() {
	try {
		const raw = window.localStorage.getItem( STORED_LOCATION_KEY );
		if ( ! raw ) {
			return null;
		}

		const parsed = JSON.parse( raw );
		const lat = Number( parsed?.lat );
		const lng = Number( parsed?.lng );

		if ( Number.isNaN( lat ) || Number.isNaN( lng ) ) {
			return null;
		}

		return { lat, lng };
	} catch ( error ) {
		console.warn( 'Unable to read stored user location.', error );
		return null;
	}
}

/**
 * Saves the user location to localStorage for subsequent page loads.
 *
 * @param {google.maps.LatLngLiteral} userLatLng Location to persist.
 * @return {void}
 */
function saveStoredLocation( userLatLng ) {
	try {
		window.localStorage.setItem(
			STORED_LOCATION_KEY,
			JSON.stringify( {
				lat: userLatLng.lat,
				lng: userLatLng.lng,
				savedAt: Date.now(),
			} )
		);
	} catch ( error ) {
		console.warn( 'Unable to save user location.', error );
	}
}

/**
 * Places or moves the user-location pin and radius circle.
 *
 * On mobile, the radius is 350 feet and the view is centered at INITIAL_ZOOM.
 *
 * @param {google.maps.LatLngLiteral} userLatLng Target lat/lng.
 * @param {Object} [options] Placement options.
 * @param {boolean} [options.fitBounds=false] Whether to fit/recenter the map view.
 * @return {Promise<void>}
 */
export async function setUserLocation( userLatLng, options = {} ) {
	const { fitBounds = false } = options;

	if ( ! map || ! userLatLng ) {
		return;
	}

	const radiusMeters = getLocationRadiusMeters();

	if ( ! radiusCircle ) {
		setRadiusCircle(
			new google.maps.Circle( {
				center: userLatLng,
				radius: radiusMeters,
				map: map,
				clickable: false,
				fillColor: '#4285F4',
				fillOpacity: 0.08,
				strokeColor: '#4285F4',
				strokeOpacity: 0.4,
				strokeWeight: 1,
			} )
		);
	} else {
		radiusCircle.setCenter( userLatLng );
		radiusCircle.setRadius( radiusMeters );
		radiusCircle.setMap( map );
	}

	if ( fitBounds ) {
		// Mobile: keep zoom at INITIAL_ZOOM. Fitting a ~900ft box was forcing ~16.
		if ( isMobileViewport() ) {
			map.setCenter( userLatLng );
			map.setZoom( INITIAL_ZOOM );
		} else {
			map.fitBounds( radiusCircle.getBounds() );
		}
	}

	let MarkerClass = AdvancedMarkerElementClass;
	let PinClass = PinElementClass;

	if ( ! MarkerClass || ! PinClass ) {
		const { AdvancedMarkerElement, PinElement } =
			await google.maps.importLibrary( 'marker' );
		setMarkerClasses( AdvancedMarkerElement, PinElement );
		MarkerClass = AdvancedMarkerElement;
		PinClass = PinElement;
	}

	if ( ! userLocationMarker ) {
		const pin = new PinClass( {
			background: '#4285F4',
			glyphColor: '#ffffff',
			borderColor: '#000000',
		} );
		setUserLocationMarker(
			new MarkerClass( {
				map: map,
				position: userLatLng,
				title: 'Your location',
				content: pin.element,
			} )
		);
	} else {
		userLocationMarker.position = userLatLng;
		userLocationMarker.map = map;
	}

	updateMarkerVisibility();
}

/**
 * Centers the map on the user's location and fits the view to the configured radius.
 *
 * @param {GeolocationPosition} position Browser geolocation result.
 * @return {Promise<void>}
 */
export async function centerOnUserLocation( position ) {
	let userLatLng = {
		lat: position.coords.latitude,
		lng: position.coords.longitude,
	};

	// Local override for development when device GPS is far from the canvass area.
	if ( window.location.hostname === 'sumy.voters.test' ) {
		userLatLng = { lat: 40.465718, lng: -86.9098189 };
	}

	saveStoredLocation( userLatLng );
	await setUserLocation( userLatLng, { fitBounds: true } );
}

/**
 * Moves the user location pin and radius to a map click position.
 *
 * @param {google.maps.MapMouseEvent} event Map click event.
 * @return {void}
 */
export function handleMapClick( event ) {
	if ( ! event.latLng ) {
		return;
	}

	const userLatLng = {
		lat: event.latLng.lat(),
		lng: event.latLng.lng(),
	};

	saveStoredLocation( userLatLng );
	setUserLocation( userLatLng );
}

/**
 * Requests a fresh GPS fix and updates the stored location.
 *
 * @return {void}
 */
function requestFreshUserLocation() {
	if ( ! navigator.geolocation ) {
		handleGeolocationError( {
			code: 0,
			message: 'Geolocation is not supported by this browser.',
		} );
		return;
	}

	navigator.geolocation.getCurrentPosition(
		centerOnUserLocation,
		handleGeolocationError,
		GEO_OPTIONS
	);
}

/**
 * Centers the map from stored location when available, otherwise asks the browser.
 *
 * Pass `{ force: true }` (e.g. My location button) to always request a fresh GPS fix.
 *
 * @param {Object} [options] Request options.
 * @param {boolean} [options.force=false] Skip localStorage and call geolocation.
 * @return {void}
 */
export function requestUserLocation( options = {} ) {
	const { force = false } = options;

	if ( ! force ) {
		const stored = loadStoredLocation();
		if ( stored ) {
			setUserLocation( stored, { fitBounds: true } );
			return;
		}
	}

	requestFreshUserLocation();
}

/**
 * Handles geolocation errors. Map stays on marker bounds or default center.
 *
 * @param {GeolocationPositionError|Object} error Geolocation error.
 * @return {void}
 */
export function handleGeolocationError( error ) {
	const messages = {
		0: 'Geolocation is not supported by this browser.',
		1: 'Location permission denied. Use the My location button to try again.',
		2: 'Location information is unavailable.',
		3: 'Location request timed out.',
	};
	console.warn( messages[ error.code ] || error.message || error );
}
